import { Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";

const HELP = {
  ISO_4um: [
    "Aantal zeer kleine vuildeeltjes vanaf 4 µm. Een lagere ISO-klasse betekent schonere olie.",
    "Quantity of very small contamination particles from 4 µm. A lower ISO class means cleaner oil.",
  ],
  ISO_6um: [
    "Aantal middelgrote vuildeeltjes vanaf 6 µm. Een lagere ISO-klasse is beter.",
    "Quantity of medium contamination particles from 6 µm. A lower ISO class is better.",
  ],
  ISO_14um: [
    "Aantal grotere vuildeeltjes vanaf 14 µm die slijtage en klepstoring kunnen veroorzaken. Lager is beter.",
    "Quantity of larger particles from 14 µm that can cause wear and valve problems. Lower is better.",
  ],
  low_saturation_pct_rh: [
    "Vochtverzadiging van de olie. Hoe hoger het percentage, hoe dichter de olie bij vrije watervorming komt. Lager is beter.",
    "Oil moisture saturation. The higher the percentage, the closer the oil is to forming free water. Lower is better.",
  ],
  temperature_c: [
    "Olietemperatuur gemeten door de CS1000. Beoordeel deze samen met de normale bedrijfstemperatuur van de installatie.",
    "Oil temperature measured by the CS1000. Compare it with the installation's normal operating temperature.",
  ],
  hlb_temperature_c: [
    "Olietemperatuur gemeten door de HLB1400.",
    "Oil temperature measured by the HLB1400.",
  ],
  conductivity_ns_m: [
    "Elektrische geleidbaarheid van de olie. Vooral een duidelijke verandering ten opzichte van de eigen trend kan op veroudering of verontreiniging wijzen.",
    "Electrical conductivity of the oil. A clear change from its own trend may indicate ageing or contamination.",
  ],
  change_conductivity_pct: [
    "Procentuele verandering in geleidbaarheid ten opzichte van de referentie. Een sterke verandering verdient onderzoek.",
    "Percentage change in conductivity from the reference. A large change should be investigated.",
  ],
  relative_permittivity: [
    "Relatieve permittiviteit is een olie-eigenschap die helpt veranderingen in olieconditie te herkennen. Kijk vooral naar de trend.",
    "Relative permittivity is an oil property that helps identify changes in oil condition. Focus on the trend.",
  ],
  profile_limit: [
    "De inschakelgrens van het gekozen systeemprofiel. Overschrijding van één ISO-waarde betekent dat filteren wordt aanbevolen.",
    "The start limit for the selected system profile. Exceeding one ISO value means filtering is advised.",
  ],
  process_status: [
    "Toont wat de RevPi nu doet: wachten, opwarmen, meten, filteren of een storing vasthouden.",
    "Shows what the RevPi is doing now: waiting, warming up, measuring, filtering or holding a fault.",
  ],
  filter_advised: [
    "Minimaal één gemeten ISO-waarde ligt boven de grens van het gekozen systeemprofiel.",
    "At least one measured ISO value exceeds the selected system profile limit.",
  ],
  online: [
    "Online betekent dat het portaal recent een geldige synchronisatie van de RevPi heeft ontvangen.",
    "Online means the portal recently received a valid synchronization from the RevPi.",
  ],
  next_measurement: [
    "Het eerstvolgende geplande meetmoment zoals dit door de RevPi is berekend. Een handmatige meting kan eerder plaatsvinden.",
    "The next scheduled measurement time calculated by the RevPi. A manual measurement may occur sooner.",
  ],
  data_usage: [
    "Werkelijk ontvangen en verzonden verkeer op de internetinterface van de RevPi. De registratie begint zodra softwareversie 2.4.2 actief is.",
    "Actual received and transmitted traffic on the RevPi internet interface. Tracking starts when software version 2.4.2 becomes active.",
  ],
};

export function InfoHint({ helpKey, nl, en, className = "" }) {
  const { t } = useLang();
  const pair = HELP[helpKey] || [nl, en];
  const text = pair?.[0] ? t(pair[0], pair[1] || pair[0]) : "";
  if (!text) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" onClick={(event) => event.stopPropagation()} aria-label={t("Uitleg tonen", "Show explanation")} className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary ${className}`}>
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 text-xs leading-5" onClick={(event) => event.stopPropagation()}>{text}</PopoverContent>
    </Popover>
  );
}

const GUIDE_ITEMS = [
  ["ISO 4 µm", "ISO_4um"], ["ISO 6 µm", "ISO_6um"], ["ISO 14 µm", "ISO_14um"],
  ["Waterverzadiging", "low_saturation_pct_rh"], ["Geleidbaarheid", "conductivity_ns_m"],
  ["Grenswaarde", "profile_limit"], ["Processtatus", "process_status"],
];

export function ValueGuideButton() {
  const { t } = useLang();
  return (
    <Dialog>
      <DialogTrigger asChild><Button variant="outline" size="sm" className="gap-2"><Info className="h-4 w-4" />{t("Uitleg waarden", "Value guide")}</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle>{t("Wat betekenen de waarden?", "What do the values mean?")}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{t("Deze uitleg helpt bij een eerste beoordeling. Kijk bij afwijkingen altijd naar de trend en de eisen van de hydraulische installatie.", "This guide helps with an initial assessment. For deviations, always consider the trend and the hydraulic installation's requirements.")}</p>
        <div className="divide-y divide-border rounded-lg border border-border">
          {GUIDE_ITEMS.map(([label, key]) => <div key={key} className="p-3"><div className="text-sm font-bold">{key === "low_saturation_pct_rh" ? t(label, "Moisture saturation") : key === "conductivity_ns_m" ? t(label, "Conductivity") : key === "profile_limit" ? t(label, "Limit") : key === "process_status" ? t(label, "Process status") : label}</div><p className="mt-1 text-xs leading-5 text-muted-foreground">{t(HELP[key][0], HELP[key][1])}</p></div>)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
